import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import CTABand from "@/components/recruit/CTABand";

export default function MessagePage() {
  useEffect(() => { document.title = "代表メッセージ | ACOE 採用サイト"; }, []);

  return (
    <div data-testid="message-page">
      {/* ─── HERO ─── */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-acoe-charcoal" data-testid="message-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-6">Founder Message</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.15] max-w-3xl">
            代表メッセージ
          </h1>
        </div>
      </section>

      {/* ─── MESSAGE BODY ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="message-body">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Founder profile - sticky on desktop */}
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <div className="aspect-[3/4] bg-acoe-paper border border-acoe-border overflow-hidden mb-6">
                  <img
                    src="https://customer-assets.emergentagent.com/job_21a5e501-0b8f-4c90-82cf-519f8b25a6ce/artifacts/vrv68cds_CEO%20photo.png"
                    alt="シリカン アサワ - 代表取締役"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h2 className="font-heading font-bold text-xl text-acoe-charcoal">シリカン アサワ</h2>
                <p className="text-sm text-acoe-gold font-heading font-semibold mt-1">代表取締役 / Founder & CEO</p>
                <div className="mt-4 text-sm text-acoe-text leading-relaxed">
                  <p>立命館アジア太平洋大学（APU）卒業。観光・ホスピタリティ業界での経験を経て、2019年にACOEを創業。</p>
                  <p className="mt-2">東京を拠点に、ファミリー向けライフスタイル宿泊ブランドを一から立ち上げ、7年間で複数物件まで拡大。</p>
                </div>
              </div>
            </div>

            {/* Letter content */}
            <div className="lg:col-span-8">
              <div className="max-w-2xl">
                <p className="font-accent italic text-2xl md:text-3xl text-acoe-charcoal leading-snug mb-12">
                  「この仲間と、次の100棟を。」
                </p>

                <div className="space-y-6 text-acoe-text leading-[1.9]">
                  <p>
                    ACOEを創業して7年が経ちました。
                  </p>
                  <p>
                    最初の物件は、自分で見つけ、自分で交渉し、自分でデザインを考え、自分で運営を始めました。
                    当時は「本当にうまくいくのだろうか」という不安しかなかったのを覚えています。
                  </p>
                  <p>
                    しかし、一つひとつの物件で「家族が一緒に思い出を作れる空間」を追求し続けた結果、
                    ゲストからの評価は積み上がり、東京で複数の物件を運営するブランドに成長しました。
                    2025年のAirbnb評価4.96、品質スコア96.9%。この数字は、私たちが基本を徹底してきた証だと思っています。
                  </p>
                  <p>
                    ACOEが次に目指しているのは、「最も愛されるファミリー向けホスピタリティブランド」になることです。
                    そのためには、運営力だけでなく、ACOEブランドで運営できる物件を広げていく力が必要です。
                  </p>
                  <p>
                    これまで物件の開拓は、すべて私自身が行ってきました。
                    オーナーとの信頼関係を一つずつ築きながら、ここまで来ることができました。
                    しかし、次の成長フェーズでは、私一人の力では限界があります。
                  </p>
                  <p>
                    だからこそ、同じ目線で市場を見て、パイプラインを開拓し、
                    ACOEの成長を一緒にリアルタイムで実感してくれる仲間を探しています。
                  </p>
                  <p>
                    大きな組織ではありません。
                    でも、だからこそ一人の力がブランド全体に影響を与えられるフェーズです。
                    優秀な人材が最大限に力を発揮できる環境を、会社として整えていきます。
                  </p>
                  <p>
                    ACOEの物件を増やすということは、家族の思い出が生まれる場所を増やすということ。
                    そのビジョンに共感してくれる方と、ぜひ一度お話ししたいです。
                  </p>
                </div>

                <div className="mt-12 pt-8 border-t border-acoe-border">
                  <p className="font-heading font-bold text-acoe-charcoal">シリカン アサワ</p>
                  <p className="text-sm text-acoe-muted mt-1">株式会社セイライグループジャパン 代表取締役</p>
                </div>

                <div className="mt-12 flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/recruit/contact"
                    className="inline-flex items-center justify-center rounded-full bg-acoe-charcoal text-acoe-bg px-8 py-4 font-heading font-semibold tracking-wide hover:bg-acoe-gold transition-colors"
                    data-testid="message-cta-primary"
                  >
                    お問い合わせ
                  </Link>
                  <Link
                  to="/recruit/work"
                  className="inline-flex items-center gap-2 text-acoe-charcoal font-heading font-semibold hover:text-acoe-gold transition-colors group"
                  data-testid="message-story-link"
                >
                  仕事の詳細を見る
                  <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
                </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <CTABand variant="dark" />
    </div>
  );
}
